import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Radio } from '@mui/material';
import { KIND_COLOR, kindIcon } from './constants';
import { useMoveProject } from '../../hooks/useProjects';

/**
 * Move-project dialog: pick a new parent (or root). Self + descendants disabled.
 * Props: open, onClose, project (the one being moved), flatProjects (with depth), disabledIds (Set)
 */
export default function MoveProjectDialog({ open, onClose, project, flatProjects = [], disabledIds }) {
  const moveMut = useMoveProject();
  const [choice, setChoice] = useState(null);

  if (!project) return null;

  const submit = async () => {
    await moveMut.mutateAsync({ id: project.id, parent_id: choice });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Move "{project.name}"</DialogTitle>
      <DialogContent>
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          <Box
            onClick={() => setChoice(null)}
            sx={{ display: 'flex', alignItems: 'center', p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <Radio size="small" checked={choice === null} />
            <Typography variant="body2">(root — no parent)</Typography>
          </Box>
          {flatProjects.map((p) => {
            const disabled = disabledIds?.has(p.id) || p.id === project.id;
            const Icon = kindIcon(p.kind);
            return (
              <Box
                key={p.id}
                onClick={() => !disabled && setChoice(p.id)}
                sx={{
                  display: 'flex', alignItems: 'center',
                  pl: `${8 + (p.depth || 0) * 16}px`, pr: 1, py: 0.75,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.35 : 1,
                  '&:hover': { bgcolor: disabled ? 'transparent' : 'action.hover' },
                }}
              >
                <Radio size="small" checked={choice === p.id} disabled={disabled} />
                <Icon sx={{ fontSize: 18, color: KIND_COLOR[p.kind] || 'text.secondary', mr: 0.5 }} />
                <Typography variant="body2">{p.name}</Typography>
                {p.id === project.parent_id && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>(current)</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={moveMut.isPending}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={moveMut.isPending || choice === project.parent_id}>
          Move here
        </Button>
      </DialogActions>
    </Dialog>
  );
}
